import React from "react";
import { Button, CheckboxCardGroup, Input } from "@porchpass/ui";
import type { EmailRecipient } from "src/api/preApproval/types";
import { createRecipient, isValidEmail } from "./recipientUtils";

interface RecipientListSectionProps {
  title: string;
  group: "borrower" | "dealer";
  recipients: EmailRecipient[];
  selectedIds: string[];
  newEmail: string;
  addEmailLabel: string;
  addButtonLabel: string;
  onRecipientsChange: (recipients: EmailRecipient[]) => void;
  onSelectedIdsChange: (selectedIds: string[]) => void;
  onNewEmailChange: (email: string) => void;
  onValidationError: (message: string | null) => void;
}

export function RecipientListSection({
  title,
  group,
  recipients,
  selectedIds,
  newEmail,
  addEmailLabel,
  addButtonLabel,
  onRecipientsChange,
  onSelectedIdsChange,
  onNewEmailChange,
  onValidationError,
}: RecipientListSectionProps) {
  const handleAddRecipient = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed) {
      return;
    }
    if (!isValidEmail(trimmed)) {
      onValidationError("Enter a valid email address.");
      return;
    }
    const normalized = trimmed.toLowerCase();
    if (recipients.some((recipient) => recipient.email === normalized)) {
      onValidationError("That email is already in this list.");
      return;
    }

    const recipient = createRecipient(normalized, group);
    onRecipientsChange([...recipients, recipient]);
    onSelectedIdsChange([...selectedIds, recipient.id]);
    onNewEmailChange("");
    onValidationError(null);
  };

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-[#101827]">{title}</h3>
      {recipients.length > 0 ? (
        <CheckboxCardGroup
          size="s"
          options={recipients.map((recipient) => ({
            value: recipient.id,
            label: recipient.email,
          }))}
          value={selectedIds}
          onValueChange={(value) => {
            onSelectedIdsChange(value);
            onValidationError(null);
          }}
        />
      ) : (
        <p className="text-sm text-[#9AA2B2]">No recipients added yet.</p>
      )}
      <form
        onSubmit={(event) => {
          handleAddRecipient(event);
        }}
        className="flex items-end gap-2"
      >
        <Input
          label={addEmailLabel}
          type="email"
          value={newEmail}
          onChange={(event) => {
            onNewEmailChange(event.target.value);
            onValidationError(null);
          }}
          className="flex-1 min-w-0"
          clearable={false}
        />
        <Button type="submit" size="s" variant="secondary" className="shrink-0">
          {addButtonLabel}
        </Button>
      </form>
    </section>
  );
}
